/**
 * Pure URL <-> language mapping for the two-language route table in
 * routing/raksme.mainRouting.ts. Kept free of Angular imports so it can be
 * reasoned about and tested on its own.
 */

export type Lang = 'en' | 'ar';

/** The URL segment that marks the Arabic variant of a route. */
export const AR = 'ar';

/**
 * Route roots that mount their own child routes, so the `ar` marker sits
 * immediately after the root instead of at the end of the URL:
 * `/my-account/ar/dashboard`, not `/my-account/dashboard/ar`.
 *
 * Every other route appends `ar` last (`/about-us/raksme/ar`,
 * `/our-services/:slug/form/:id/edit/ar`), and my-account is currently the only
 * module in routing/ that declares `children`.
 */
export const NESTED_ROUTE_ROOTS = ['my-account'];

export interface LanguageUrls {
  lang: Lang;
  enUrl: string;
  arUrl: string;
}

/**
 * Given the router's current URL, report which language it is and where each
 * of the two options should point.
 *
 * Language is decided by an exact segment match on `ar`, not by a substring
 * test: `/business-directory/12/architects` is English, though it contains the
 * characters "/ar".
 */
export function resolveLanguageUrls(url: string): LanguageUrls {
  const splitAt = firstIndexOf(url || '/', ['?', '#']);
  const source = url || '/';
  const path = splitAt === -1 ? source : source.substring(0, splitAt);
  const tail = splitAt === -1 ? '' : source.substring(splitAt);

  const segments = path.split('/').filter(segment => segment.length > 0);
  const arIndex = segments.indexOf(AR);

  // Language-neutral form of the current route.
  const base = arIndex > -1
    ? segments.slice(0, arIndex).concat(segments.slice(arIndex + 1))
    : segments.slice();

  return {
    lang: arIndex > -1 ? 'ar' : 'en',
    enUrl: buildUrl(base, tail),
    arUrl: buildUrl(withAr(base), tail)
  };
}

/** Insert the `ar` marker at the position this route family expects. */
function withAr(base: string[]): string[] {
  const segments = base.slice();
  if (segments.length > 0 && NESTED_ROUTE_ROOTS.indexOf(segments[0]) > -1) {
    segments.splice(1, 0, AR);
    return segments;
  }
  segments.push(AR);
  return segments;
}

function buildUrl(segments: string[], tail: string): string {
  return '/' + segments.join('/') + tail;
}

function firstIndexOf(value: string, chars: string[]): number {
  let found = -1;
  for (const char of chars) {
    const index = value.indexOf(char);
    if (index > -1 && (found === -1 || index < found)) {
      found = index;
    }
  }
  return found;
}
